import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { number, bool, string, arrayOf } from 'prop-types';
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import imageUrls from './Urls.js';
import styles from './ImageCompositor.module.css.js';
import { MODEL_CODES, CYBERTRUCK, MODEL_3, MODEL_S, MODEL_X, MODEL_Y, ROADSTER_RD, ROADSTER_MT, SEMI } from './Constants.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var CROP = {
  w: 1400,
  h: 900,
  r: 400,
  b: 0
};
var getCrop = function getCrop(crop) {
  return crop ? "&crop=".concat(CROP.w, ",").concat(CROP.h, ",").concat(CROP.r, ",").concat(CROP.b) : '&crop=0,0,0,0';
};
var isRaven = function isRaven() {
  var mfg_options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return !!mfg_options.find(function (opt) {
    return opt.code === 'SU03';
  });
};
var getCompositorViewType = function getCompositorViewType(model, isLegacy) {
  var isModelXorS = ['ms', 'mx'].indexOf(model) >= 0;
  if (isModelXorS) {
    if (isLegacy) {
      return 'STUD_3QTR';
    }
    return 'STUD_3QTR_V2';
  }
  return 'STUD_3QTR';
};
var useImage = function useImage(_ref) {
  var isVehicle = _ref.isVehicle,
    isEcommerce = _ref.isEcommerce,
    model = _ref.model,
    optionCodes = _ref.optionCodes,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 400 : _ref$size,
    _ref$showBackground = _ref.showBackground,
    showBackground = _ref$showBackground === void 0 ? 2 : _ref$showBackground,
    isStackedImage = _ref.isStackedImage,
    mfgCodes = _ref.mfgCodes,
    crop = _ref.crop,
    baseUrl = _ref.baseUrl;
  var source = '';
  var isLegacy = !isRaven(mfgCodes);
  var viewType = getCompositorViewType(model, isLegacy);
  if (model === MODEL_CODES[ROADSTER_RD]) {
    return imageUrls.ROADSTER_RD_IMAGE;
  }
  if (model === MODEL_CODES[ROADSTER_MT]) {
    return imageUrls.ROADSTER_MT_IMAGE;
  }
  if (model === MODEL_CODES[CYBERTRUCK]) {
    return imageUrls.CYBERTRUCK_IMAGE;
  }
  if (model === MODEL_CODES[SEMI]) {
    return "".concat(imageUrls.DIGITAL_ASSETS_URL, "/semi/semi_3qrt_view.png");
  }
  if (isEcommerce) {
    return imageUrls.ECOMMERCE_ONLY;
  }
  if (isStackedImage) {
    source = isVehicle ? imageUrls.STACKED_VEHICLE : imageUrls.STACKED_ENERGY;
  } else if (isVehicle && (!model || !optionCodes) || !isVehicle && (!model || !(imageUrls !== null && imageUrls !== void 0 && imageUrls[model === null || model === void 0 ? void 0 : model.toUpperCase()]))) {
    source = isVehicle ? imageUrls.DEFAULT_VEHICLE_IMAGE : imageUrls.DEFAULT_ENERGY_IMAGE;
  } else {
    var completeUrl = "".concat(baseUrl).concat(imageUrls.STATIC_ASSET, "?options=").concat(optionCodes).concat(getCrop(crop), "&model=").concat(model, "&view=").concat(viewType, "&size=").concat(size, "&bkba_opt=").concat(showBackground, "&context=design_studio_2");
    source = isVehicle ? completeUrl : imageUrls === null || imageUrls === void 0 ? void 0 : imageUrls[model === null || model === void 0 ? void 0 : model.toUpperCase()];
  }
  return source;
};
var ImageCompositor = function ImageCompositor(props) {
  var id = props.id,
    model = props.model,
    className = props.className,
    productType = props.productType,
    width = props.width,
    height = props.height,
    _props$crop = props.crop,
    crop = _props$crop === void 0 ? false : _props$crop;
  var isVehicle = (productType === null || productType === void 0 ? void 0 : productType.toUpperCase()) === 'VEHICLE';
  var isEcommerce = (productType === null || productType === void 0 ? void 0 : productType.toUpperCase()) === 'ECOMMERCE';
  var imageUrl = useImage(_objectSpread(_objectSpread({}, props), {}, {
    isVehicle: isVehicle,
    isEcommerce: isEcommerce,
    productType: productType === null || productType === void 0 ? void 0 : productType.toUpperCase()
  }));
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    imgSource = _useState2[0],
    setImgSource = _useState2[1];
  useEffect(function () {
    setImgSource(imageUrl);
  }, [imageUrl]);
  if (!imgSource) return null;
  return /*#__PURE__*/React.createElement("div", {
    className: classnames(_defineProperty({}, styles['tw-react--margin-left--2x'], (imgSource.indexOf('static-assets.tesla.com') === -1 || [MODEL_CODES[CYBERTRUCK], MODEL_CODES[MODEL_3], MODEL_CODES[MODEL_S], MODEL_CODES[MODEL_X], MODEL_CODES[MODEL_Y], MODEL_CODES[ROADSTER_RD], MODEL_CODES[ROADSTER_MT]].includes(model)) && crop))
  }, /*#__PURE__*/React.createElement("img", {
    src: imgSource,
    alt: "composite from ".concat(model),
    id: id,
    width: width,
    height: height,
    className: classnames(className, _defineProperty(_defineProperty(_defineProperty({}, styles['tw-react--image-compositor--zoom'], isVehicle && !['mx'].includes(model)), styles['tw-react--image-compositor--zoom-in'], !isVehicle), styles['tw-react--image-compositor--zoom-out'], [MODEL_CODES[CYBERTRUCK], MODEL_CODES[ROADSTER_RD], MODEL_CODES[ROADSTER_MT]].includes(model))),
    onError: function onError() {
      var img;
      if (isEcommerce) {
        img = imageUrls.ECOMMERCE_ONLY;
      } else if (isVehicle) {
        img = imageUrls.DEFAULT_VEHICLE_IMAGE;
      } else {
        img = imageUrls.DEFAULT_ENERGY_IMAGE;
      }
      setImgSource(img);
    }
  }));
};
useImage.propTypes = {
  size: number,
  showBackground: number,
  isStackedImage: bool,
  optionCodes: string.isRequired,
  mfgCodes: arrayOf(string).isRequired,
  baseUrl: string
};
useImage.defaultProps = {
  size: 400,
  showBackground: 2,
  isStackedImage: false,
  baseUrl: ''
};
ImageCompositor.propTypes = {
  id: string,
  model: string.isRequired,
  className: string,
  productType: string,
  width: number.isRequired,
  height: number.isRequired,
  crop: bool
};
ImageCompositor.defaultProps = {
  id: '',
  className: '',
  productType: '',
  crop: false
};

export { ImageCompositor as default, getCompositorViewType, isRaven, useImage };
